import type { SymbolAsset } from '../symbols/model';
import { DEFAULT_BASEMAP_CONFIGURATION, type BasemapConfiguration } from '../basemaps/catalog';
import { createDefaultComposition, type CompositionDraft } from '../composition/model';
import type { SourceDescriptor, SourceId } from '../data/model';
import type { LayerDisplayFilter } from '../displayFilters/displayFilter';
import type { LayerLabelConfiguration } from '../labels/model';
import type { MapView } from '../map/model';
import { DEFAULT_MAP_BACKGROUND_COLOUR } from '../map/presentation';
import type { LayerPopupConfiguration } from '../popups/model';
import type { VectorLayerStyle } from '../styles/model';
import type { JoinConfig } from '../joins/model';
import type { AnnotationId, AnnotationLayer } from '../annotations/model';

export type LayerId = string;

export const DEFAULT_PROJECT_TITLE = 'Untitled map';

export interface LayerDefinition {
  readonly id: LayerId;
  readonly sourceId: SourceId;
  readonly name: string;
  readonly visible: boolean;
  readonly displayFilter: LayerDisplayFilter | null;
  readonly style: VectorLayerStyle;
  readonly labels: LayerLabelConfiguration;
  readonly popup: LayerPopupConfiguration;
  /** Non-destructive authored table relationships, evaluated in this order. */
  readonly joins: readonly JoinConfig[];
}

export interface ProjectMapState {
  readonly view: MapView;
  readonly basemap: BasemapConfiguration;
  readonly backgroundColour: string;
}

/**
 * Minimal in-memory domain model for the foundation. This is deliberately not
 * the versioned `.placeio` schema, which belongs to the persistence boundary.
 */
export interface ProjectModelDraft {
  readonly metadata: {
    readonly title: string;
  };
  readonly map: ProjectMapState;
  readonly composition: CompositionDraft;
  readonly sources: readonly SourceDescriptor[];
  readonly symbolAssets: readonly SymbolAsset[];
  /** Authored visual stacking order, topmost layer first. */
  readonly layers: readonly LayerDefinition[];
  /** Named geographic presentation layers, topmost first and distinct from GIS sources. */
  readonly annotationLayers: readonly AnnotationLayer[];
  /** The sole stored Focus Area allowed to apply a fade effect, or none. */
  readonly activeFocusAreaId: AnnotationId | null;
}

const NEW_PROJECT_BASEMAP: BasemapConfiguration = {
  ...DEFAULT_BASEMAP_CONFIGURATION,
  presetId: 'dropmap-light',
  labelPresentation: 'follow-basemap',
};

export function createEmptyProject(): ProjectModelDraft {
  const view: MapView = {
    center: [0, 20],
    zoom: 1.5,
    bearing: 0,
    pitch: 0,
  };
  return {
    metadata: { title: DEFAULT_PROJECT_TITLE },
    map: {
      view,
      basemap: NEW_PROJECT_BASEMAP,
      backgroundColour: DEFAULT_MAP_BACKGROUND_COLOUR,
    },
    composition: createDefaultComposition(view, 'Untitled map'),
    sources: [],
    symbolAssets: [],
    layers: [],
    annotationLayers: [],
    activeFocusAreaId: null,
  };
}
